/**
 * THE STATES THAT ARE NOT AN ANSWER — each rendered distinctly, none generic
 * (spec 16.2).
 *
 * ══ A DECLINE IS THE PRODUCT WORKING, AND THE SCREEN MUST SAY SO ═══════════
 *
 * 🔴 `DeclinedPanel` and `RefusedPanel` are NEUTRAL — `bg-muted`, a shield, and
 * never red. An agent that declines to answer a question no metric covers, or
 * refuses a question carrying a member identifier, has just done the single
 * most valuable thing in this design. Painting that red trains the operator to
 * read a correct refusal as a malfunction, and the next step after that is
 * somebody asking for the control to be relaxed.
 *
 * ══ AND FIVE ERRORS, NOT ONE BANNER ════════════════════════════════════════
 *
 * Transport, turn-failed, timed-out, 403 and 429 have different causes and
 * different remedies. `errorCopy` keeps the wording beside the state machine so
 * a new variant cannot be added without wording, and the 403 panel NAMES the
 * missing key rather than saying "access denied".
 */
import { AlertTriangle, Clock, Lightbulb, Lock, RefreshCw, Shield, Sparkles } from 'lucide-react';

import type { TurnDecline } from '../protocol';
import { declinedHeading, errorCopy, reasonCopy, type TurnErrorVariant } from '../turn-state';

export function EmptyState({
  starters,
  degraded,
  loading,
  fallback,
  onPick,
}: {
  starters: { metric_id: string; title: string; question: string }[];
  degraded: boolean;
  loading: boolean;
  fallback: readonly string[];
  onPick: (question: string) => void;
}) {
  // CHIPS, NOT CARDS. The six paragraph cards this replaced repeated the page
  // subtitle almost verbatim and pushed the composer below the fold. A starter
  // is a shortcut, not documentation: the TITLE is the affordance and the whole
  // control is the hit target — the old markup put a smaller <button> inside a
  // bordered <li>, so a click in the visible padding silently did nothing.
  const chip = (label: string, question: string, key: string) => (
    <li key={key}>
      <button
        type="button"
        onClick={() => onPick(question)}
        title={question}
        className="rounded-full border bg-muted/40 px-4 py-2 text-sm text-foreground/90 transition-colors hover:border-primary/40 hover:bg-muted"
      >
        {label}
      </button>
    </li>
  );

  return (
    <div>
      {degraded && (
        <p className="mb-3 flex items-start gap-2 rounded-md border border-yellow-500/40 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          The catalogue could not be reached, so these are generic prompts rather than the
          metrics your role can read.
        </p>
      )}

      <ul className="flex flex-wrap gap-2">
        {loading && !degraded
          ? Array.from({ length: 4 }).map((_, index) => (
              <li
                key={index}
                className="h-9 w-40 motion-safe:animate-pulse rounded-full bg-muted"
              />
            ))
          : degraded || starters.length === 0
            ? fallback.map((question) => chip(question, question, question))
            : starters.map((starter) => chip(starter.title, starter.question, starter.metric_id))}
      </ul>
    </div>
  );
}

/**
 * THE WAIT IS THE WORK, SO SHOW THE WORK.
 *
 * This used to be three grey rectangles. A turn takes 13-19 seconds against a
 * live model, and for all of it the operator had no signal that anything was
 * happening — measured on DEV 2026-08-27, three turns at 12.7s, 14.4s and
 * 19.3s, every one of them behind a static skeleton.
 *
 * The phases below are the same four `ToolTimeline` already names once the
 * poll returns a phase; this is the moment BEFORE the first poll answers, so
 * it shows the ladder at rest rather than pretending to know a phase it has
 * not been told.
 */
export function LoadingState() {
  const steps = [
    { icon: Shield, label: 'Screening the question' },
    { icon: Sparkles, label: 'Choosing how to answer' },
    { icon: Lightbulb, label: 'Reading the data' },
    { icon: Clock, label: 'Writing the answer' },
  ];
  return (
    <div className="rounded-lg border bg-card p-5" aria-busy="true">
      <ol className="space-y-2.5">
        {steps.map((step, index) => (
          <li key={step.label} className="flex items-center gap-2.5 text-sm">
            {/* 🔴 NO OPACITY ON THE PENDING STEPS. This ladder is on screen for
                the whole of every turn, and `text-muted-foreground/50` measured
                1.97:1 against the card in light mode — WCAG AA wants 4.5:1 for
                text and 3:1 for a meaningful icon. NOTHING below /100 reaches
                either: /70 is 2.72:1 and /80 is 3.25:1, both computed from this
                app's own `--muted-foreground` token, worst case across light and
                dark. So the recession comes from COLOUR and WEIGHT, which cost
                no legibility: the live step is foreground and medium, the
                pending steps are the muted token at full strength (4.75:1 light,
                7.80:1 dark). */}
            <step.icon
              className={`h-4 w-4 shrink-0 ${index === 0 ? 'text-primary' : 'text-muted-foreground'}`}
              aria-hidden
            />
            <span
              className={
                index === 0
                  ? 'font-medium text-foreground motion-safe:animate-pulse'
                  : 'text-muted-foreground'
              }
            >
              {step.label}
            </span>
          </li>
        ))}
      </ol>
      <p className="sr-only" role="status">
        Starting the turn.
      </p>
    </div>
  );
}

export function StoppedPanel({ onReset }: { onReset?: () => void }) {
  return (
    <div className="rounded-lg border bg-muted/50 p-4 text-sm" role="note">
      <p className="font-medium">Stopped.</p>
      <p className="mt-1 text-muted-foreground">
        The turn may still finish server-side, but this screen can no longer collect its result — there is no way to reopen a turn once its poll has stopped. A
        client abort cannot stop the worker, and this panel will not pretend otherwise.
      </p>
      {/* Absent on a HISTORY entry: the outcome still renders, the action does
          not. And the label is honest — "Ask something else" CLEARS the whole
          session record (reset()), which "ask something else" does not imply. */}
      {onReset !== undefined && (
        <button
          type="button"
          onClick={onReset}
          className="mt-2 text-xs text-primary underline-offset-2 hover:underline"
        >
          Start over — this clears the questions above
        </button>
      )}
    </div>
  );
}

export function SlowTurnHint({ message }: { message: string }) {
  return (
    <p className="flex items-start gap-2 text-xs text-muted-foreground" role="note">
      <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      {message}
    </p>
  );
}

/** State 6 — NEUTRAL, never red. */
export function DeclinedPanel({
  decline,
  onPick,
  tablesBelow = false,
}: {
  decline: TurnDecline;
  onPick: (question: string) => void;
  /**
   * The turn put rows on the operator's screen before the model declined. The
   * heading must then not say "Not answered": the tables under it are the
   * answer, and only the model's sentence is missing.
   */
  tablesBelow?: boolean;
}) {
  const suggestions = decline.suggested_metric_ids ?? [];
  return (
    <div className="rounded-lg border bg-muted/50 p-4" role="note">
      <div className="flex items-center gap-2">
        <Shield className="h-4 w-4 text-muted-foreground" aria-hidden />
        <h3 className="text-sm font-semibold">
          {declinedHeading(decline.kind, tablesBelow)}
        </h3>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{decline.message}</p>
      <p className="mt-2 text-xs text-muted-foreground">{reasonCopy(decline.reason_code)}</p>
      {suggestions.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium text-muted-foreground">Metrics that might fit</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {suggestions.map((metricId) => (
              <button
                key={metricId}
                type="button"
                onClick={() => onPick(`Show me ${metricId}`)}
                className="rounded-full border bg-background px-2.5 py-1 font-mono text-[11px] transition-colors hover:border-primary/50"
              >
                {metricId}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The pre-model input gate refused. ZERO model requests were made and the
 * question never left the estate — which is worth saying out loud, because it
 * is the difference between a control and a promise.
 */
export function RefusedPanel({
  reasonCode,
  message,
}: {
  reasonCode: string;
  /**
   * The BACKEND's canned refusal string. Uniform for every gate reason on
   * purpose — see the block in `turn-state.ts` above `REASON_COPY`. Prefer it
   * over anything derived from the code.
   */
  message?: string;
}) {
  return (
    <div className="rounded-lg border bg-muted/50 p-4" role="note">
      <div className="flex items-center gap-2">
        <Lock className="h-4 w-4 text-muted-foreground" aria-hidden />
        <h3 className="text-sm font-semibold">Not sent</h3>
      </div>
      <p className="mt-2 text-sm">
        {message ??
          'This assistant answers questions about aggregates only. Remove any member ' +
            'name, e-mail address, phone number or id and ask again.'}
      </p>
      <p className="mt-2 font-mono text-[11px] text-muted-foreground">{reasonCode}</p>
      <p className="mt-2 text-xs text-muted-foreground">
        The question was screened before any model request, so nothing was sent to the
        provider and nothing about it was retained beyond the audit record of the refusal.
      </p>
    </div>
  );
}

export function ErrorPanel({
  variant,
  code,
  status,
  onRetry,
}: {
  variant: TurnErrorVariant;
  code: string;
  status: number | null;
  onRetry?: () => void;
}) {
  const copy = errorCopy({ variant, code, status });
  const neutral = variant === 'forbidden' || variant === 'rate_limited';
  const Icon = variant === 'forbidden' ? Lock : variant === 'timed_out' ? Clock : AlertTriangle;
  return (
    <div
      className={
        neutral
          ? 'rounded-lg border bg-muted/50 p-4'
          : 'rounded-lg border border-destructive/40 bg-destructive/5 p-4'
      }
      role="alert"
    >
      <div className="flex items-center gap-2">
        <Icon
          className={`h-4 w-4 ${neutral ? 'text-muted-foreground' : 'text-red-600 dark:text-red-400'}`}
          aria-hidden
        />
        <h3 className="text-sm font-semibold">{copy.title}</h3>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{copy.body}</p>
      <p className="mt-2 font-mono text-[11px] text-muted-foreground">
        {code}
        {status === null ? '' : ` · HTTP ${status}`}
      </p>
      {copy.retryable && onRetry !== undefined && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition-colors hover:bg-muted"
        >
          <RefreshCw className="h-3 w-3" aria-hidden />
          Try again
        </button>
      )}
    </div>
  );
}

/** A hint the surface shows once, under the composer, on a first visit. */
export function ScopeHint() {
  return (
    <p className="flex items-start gap-2 text-[11px] text-muted-foreground">
      <Lightbulb className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
      {/* True with and without the row-level SQL tools (owner decision 2026-10-15): the
          input gate refuses a full name, an e-mail address or a phone number in every
          environment. The old text ("naming an individual member") stopped being true
          where a first name or a member id may be asked about (input-gate.ts). */}
      Ask about members, matches, trends and comparisons. A question with a member&apos;s full
      name, e-mail address or phone number is refused before it reaches the model.
    </p>
  );
}
